import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  SCOPE_LABELS_RU,
  allowedScopesForRole,
  type ScopeKind,
} from '@/pages/team/teamLogic';
import type { ClusterOption, FarmOption } from '@/pages/team/types';

const SCOPE_ORDER: ScopeKind[] = ['company', 'cluster', 'farm'];

const SCOPE_SUB: Record<ScopeKind, string> = {
  company: 'Все кластеры и хозяйства',
  cluster: 'Один кластер и его хозяйства',
  farm: 'Одно конкретное хозяйство',
};

interface ScopeSelectorProps {
  role: string;
  scope: ScopeKind;
  scopeId: string | null;
  clusters: ClusterOption[];
  farms: FarmOption[];
  onScopeChange: (scope: ScopeKind) => void;
  onScopeIdChange: (id: string | null) => void;
  error?: string;
  /**
   * Зоны в области управляющего (AK-886, `areaScopeKinds(manage_area)`): что
   * вне её — не показываем вовсе (админ хозяйства не видит «Вся компания» и
   * «Кластер»). По умолчанию — все три.
   */
  visibleScopes?: readonly ScopeKind[];
}

/**
 * Радио-выбор зоны доступа (Вся компания / Кластер / Хозяйство) + вложенный
 * селект конкретного кластера/хозяйства. Опции, несовместимые с текущей ролью
 * (по ROLE_SCOPE_MATRIX = backend _role_scope_ok), дизейблятся; вне области
 * управляющего (`visibleScopes`) — не показываются.
 */
export function ScopeSelector({
  role,
  scope,
  scopeId,
  clusters,
  farms,
  onScopeChange,
  onScopeIdChange,
  error,
  visibleScopes = SCOPE_ORDER,
}: ScopeSelectorProps) {
  const allowed = allowedScopesForRole(role);
  const shown = SCOPE_ORDER.filter((s) => visibleScopes.includes(s));

  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-lg border border-border">
        {shown.map((s, i) => {
          const disabled = !allowed.includes(s);
          const active = scope === s;
          return (
            <div key={s}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onScopeChange(s)}
                aria-pressed={active}
                className={cn(
                  'flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors',
                  i > 0 && 'border-t border-border',
                  disabled
                    ? 'cursor-not-allowed opacity-45'
                    : 'hover:bg-accent',
                  active && !disabled && 'bg-accent-brand/10',
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 h-4 w-4 shrink-0 rounded-full border-2',
                    active && !disabled
                      ? 'border-accent-brand border-[5px]'
                      : 'border-input',
                  )}
                />
                <span className="min-w-0">
                  <span className="block text-sm text-foreground">
                    {SCOPE_LABELS_RU[s]}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {disabled ? 'Недоступно для выбранной роли' : SCOPE_SUB[s]}
                  </span>
                </span>
              </button>
              {active && s === 'cluster' && (
                <div className="border-t border-border bg-secondary/30 px-3 py-2.5">
                  <Select
                    value={scopeId ?? undefined}
                    onValueChange={(v) => onScopeIdChange(v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Выберите кластер" />
                    </SelectTrigger>
                    <SelectContent>
                      {clusters.length === 0 ? (
                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                          Нет кластеров
                        </div>
                      ) : (
                        clusters.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {active && s === 'farm' && (
                <div className="border-t border-border bg-secondary/30 px-3 py-2.5">
                  <Select
                    value={scopeId ?? undefined}
                    onValueChange={(v) => onScopeIdChange(v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Выберите хозяйство" />
                    </SelectTrigger>
                    <SelectContent>
                      {farms.length === 0 ? (
                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                          Нет хозяйств
                        </div>
                      ) : (
                        farms.map((f) => (
                          <SelectItem key={f.id} value={f.id}>
                            {f.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
